/**
 * Card — a composed content container with optional header (title + subtitle +
 * trailing action slot) and footer slots. Set `interactive` to make the whole
 * card a hover-lifting, focusable surface (e.g. a project tile).
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Optional header title (serif). */
  readonly title?: ReactNode;
  /** Optional header subtitle under the title. */
  readonly subtitle?: ReactNode;
  /** Optional element pinned to the right of the header (e.g. a menu button). */
  readonly headerAction?: ReactNode;
  /** Optional footer content (e.g. action buttons). */
  readonly footer?: ReactNode;
  /** When true the card lifts on hover and is keyboard-focusable. */
  readonly interactive?: boolean;
  /** Remove the default body padding (caller controls inner layout). */
  readonly bodyless?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    title,
    subtitle,
    headerAction,
    footer,
    interactive,
    bodyless,
    className,
    children,
    tabIndex,
    ...rest
  },
  ref,
) {
  const hasHeader = title != null || subtitle != null || headerAction != null;
  const classes = ["lc-card", interactive ? "lc-card--interactive" : null, className]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      ref={ref}
      className={classes}
      tabIndex={interactive ? (tabIndex ?? 0) : tabIndex}
      {...rest}
    >
      {hasHeader ? (
        <div className="lc-card__header">
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            {title != null ? <h3 className="lc-card__title">{title}</h3> : null}
            {subtitle != null ? <p className="lc-card__subtitle">{subtitle}</p> : null}
          </div>
          {headerAction != null ? <div>{headerAction}</div> : null}
        </div>
      ) : null}
      {children != null ? (
        bodyless ? <>{children}</> : <div className="lc-card__body">{children}</div>
      ) : null}
      {footer != null ? <div className="lc-card__footer">{footer}</div> : null}
    </div>
  );
});
